import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { FileType } from 'src/features/files/types/file';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useDashboardService } from '../api/useDashboardService';

interface LessonType {
  title: string;
  tag: {
    label: string;
    id: string;
    value: string;
    color: string;
  };
  image: {
    alt: string;
    url: string;
  };
  description: string;
  slug: string;
  isLearning?: boolean;
  total: number;
  progress?: {
    total: number;
    completed: number;
  };
  pin?: boolean;
}

export interface DocType {
  access: string;
  completed: boolean;
  count: 0;
  createdAt: string;
  createdBy: {
    firstname: string;
    lastname: string;
    email: string;
    memberId: string;
    avatar: string;
  };
  creationId: string;
  icon?: any;
  isdoc: boolean;
  owner: { firstname: string; lastname: string; email: string; memberId: string; avatar: string };
  shared: any[];
  tasks: { completed: number; total: number };
  title: string;
  type: string;
  updatedAt: string;
  updatedBy: {
    firstname: string;
    lastname: string;
  };
}
interface AssignTasks {
  assignee: string;
  assigner: string;
  column: string;
  createdAt: string;
  icon?: any;
  name: string;
  priority: string;
  title: string;
  updatedAt: string;
  type: string;
  fileId: string;
  viewId: string;
  taskId: string;
  url: string;
  _id: string;
}

interface OrgTasks {
  avatar: string;
  name: string;
  tasks: [AssignTasks];
}

interface Organization {
  org: string;
  name: string;
  avatar: string;
  selected: boolean;
}
export interface DashboardState {
  upcomingDeadlines: {
    docs: {
      name: string;
      taskName: string;
      type: string;
      date: string;
      priority: string;
      url: string;
      icon: any;
    }[];
    hasNextPage: boolean;
    hasPrevPage: boolean;
    limit: number;
    nextPage: number;
    page: number;
    pagingCounter: number;
    prevPage: number;
    totalDocs: number;
    totalPages: number;
  };
  gettingStarted: {
    academy: { lesson: boolean; template: boolean };
    workstation: { workspace: boolean; file: boolean };
  };

  inviteBlockHide: boolean;
  recommendations: LessonType[];
  orgTasks: OrgTasks[];
  files: FileType[];
  favorites: FileType[];
  organizationList: Organization[];
  keepLearning: LessonType[];
  dashboardMessage: string;
  notes: any[] | null;
  creations: any[];
  collapseFavorites: boolean;
  collapseTemplate: boolean;
  // collapseQuickNotepad: boolean;
  collapseKeepLearning: boolean;
  collapseRecommendation: boolean;
  collapseShowWorkspaces: boolean;
  collapseBlog: boolean;
  collapseRecentTasks: boolean;
  collapseFiles: boolean;

  // proTip: string;
  // whatsNew: {
  //   id: string;
  //   title: string;
  //   header: string;
  //   description: string;
  //   asset: string;
  //   link: {
  //     title: string;
  //     action: string;
  //   };
  // };
  statistics: { streak: string; level: string; items: any };
  calendarDate: [Date];
  calendars: any[];
  actions: {
    fetchDashboardData: () => void;
    fetchAcademy: () => void;
    fetchWorkstation: () => void;
    fetchUpcomingDeadlines: (page: number, limit: number) => void;
    fetchDashboardNotepad: () => void;
    fetchDashboardFiles: () => void;
    pinFile: (fileId: string, pin: boolean) => void;
    toggleCollapseFiles: (value: boolean) => void;
    toggleCollapseTemplate: (value: boolean) => void;
    toggleCollapseKeepLearning: (value: boolean) => void;
    // toggleQuickNotepad: (value: boolean) => void;
    toggleShowWorkspaces: (value: boolean) => void;
    toggleCollapseRecommendation: (value: boolean) => void;
    toggleCollapseBlog: (value: boolean) => void;
    toggleCollapseFavorites: (value: boolean) => void;
    toggleCollapseRecentTasks: (value: boolean) => void;
    authGoogleCalendars: (code: string) => void;
    fetchGoogleCalendars: (cb?: () => void) => void;
    removeGoogleCalendars: (id: string, cb?: () => void) => void;
  };
}

const getInitialValue = () => ({
  gettingStarted: {
    academy: { lesson: false, template: false },
    workstation: { workspace: false, file: false },
  },

  upcomingDeadlines: {
    docs: [],
    hasNextPage: false,
    hasPrevPage: false,
    limit: 0,
    nextPage: 0,
    page: 0,
    pagingCounter: 0,
    prevPage: 0,
    totalDocs: 0,
    totalPages: 0,
  },
  recommendations: [],
  orgTasks: [],
  organizationList: [],
  calendars: [],
  files: [],
  favorites: [],
  notes: null,
  collapseFiles: false,
  collapseTemplate: false,
  collapseKeepLearning: false,
  collapseShowWorkspaces: false,
  // collapseQuickNotepad: false,
  collapseRecommendation: false,
  collapseBlog: true,
  collapseFavorites: true,
  collapseRecentTasks: true,

  creations: [],
  dashboardMessage: '',
  dashboardCollapseSettings: {},
  inviteBlockHide: true,
  keepLearning: [],
  // proTip: '',
  // whatsNew: {
  //   id: '',
  //   title: '',
  //   header: '',
  //   description: '',
  //   asset: '',
  //   link: {
  //     title: '',
  //     action: '',
  //   },
  // },
  statistics: { streak: '', level: '', items: [] },
});

export const useDashboardStore = create<
  DashboardState,
  [['zustand/devtools', DashboardState], ['zustand/immer', DashboardState]]
>(
  devtools(
    zustandImmerMiddleware((set) => ({
      actions: {
        fetchDashboardFiles: async () => {
          const responseFetchDashboardFavoriteFiles =
            await useDashboardService().fetchDashboardFavoriteFiles();
          if (responseFetchDashboardFavoriteFiles) {
            set((s) => {
              s.favorites = responseFetchDashboardFavoriteFiles;
            });
          }

          const responseFetchDashboardFiles = await useDashboardService().fetchDashboardFiles();

          if (responseFetchDashboardFiles) {
            set((s) => {
              s.files = responseFetchDashboardFiles;
            });
          }
        },
        pinFile: (fileId: string, pin: boolean) => {
          set((s) => {
            const file = s.files.find((f) => f.id === fileId);
            if (file) {
              file.pin = pin;
            }
          });
        },
        fetchDashboardNotepad: async () => {
          const notepadData = await useDashboardService().fetchDashboardNotepad();

          set((state) => {
            state.notes = notepadData ? notepadData : [{ type: 'p', children: [{ text: '' }] }];
          });
        },

        fetchAcademy: async () => {
          await Promise.allSettled([
            (async () => {
              const responseRecommendation = await useDashboardService().fetchRecommendation();
              if (responseRecommendation) {
                set((s) => {
                  s.recommendations = responseRecommendation.data.recommendation_result;
                });
              }
            })(),
            (async () => {
              const responseFetchDashboardTemplates =
                await useDashboardService().fetchDashboardTemplates();
              if (responseFetchDashboardTemplates) {
                set((s) => {
                  s.creations = responseFetchDashboardTemplates.data;
                });
              }
            })(),
            (async () => {
              const responseFetchDashboardKeepLearning =
                await useDashboardService().fetchDashboardKeepLearning();

              if (responseFetchDashboardKeepLearning) {
                set((s) => {
                  s.keepLearning = responseFetchDashboardKeepLearning.keepLearning;
                  s.inviteBlockHide =
                    responseFetchDashboardKeepLearning.dashboard_hide_invite_block;
                });
              }
            })(),
          ]);
        },

        fetchWorkstation: async () => {
          await Promise.allSettled([
            (async () => {
              const responseFetchAssignedTask = await useDashboardService().fetchAssignedTask();
              if (responseFetchAssignedTask) {
                set((s) => {
                  s.orgTasks = responseFetchAssignedTask.data;
                });
              }
            })(),
            (async () => {
              const responseUpcomingDeadlines = await useDashboardService().fetchUpcomingDeadlines(
                1,
                10
              );

              if (responseUpcomingDeadlines) {
                set((s) => {
                  s.upcomingDeadlines = responseUpcomingDeadlines.data.data;
                });
              }
            })(),
          ]);
        },

        fetchUpcomingDeadlines: async (page: number, limit: number) => {
          const responseUpcomingDeadlines = await useDashboardService().fetchUpcomingDeadlines(
            page ?? 1,
            limit ?? 10
          );

          if (responseUpcomingDeadlines) {
            set((s) => {
              const result = { ...responseUpcomingDeadlines.data.data };
              result.docs = [...s.upcomingDeadlines.docs, ...result.docs];
              s.upcomingDeadlines = result;
            });
          }
        },

        fetchDashboardData: async () => {
          await Promise.allSettled([
            (async () => {
              const responseGettingStarted = await useDashboardService().fetchGettingStarted();
              if (responseGettingStarted) {
                set((s) => {
                  s.gettingStarted = responseGettingStarted.data;
                });
              }
            })(),
            // (async () => {
            //   const responseFetchDashboardProTip =
            //     await useDashboardService().fetchDashboardProTip();
            //   if (responseFetchDashboardProTip) {
            //     set((s) => {
            //       s.proTip = responseFetchDashboardProTip.data;
            //     });
            //   }
            // })(),
            (async () => {
              const responseFetchOrganizations = await useDashboardService().fetchOrganizations();
              if (responseFetchOrganizations) {
                set((s) => {
                  s.organizationList = responseFetchOrganizations;
                });
              }
            })(),
            (async () => {
              const responseFetchDashboardMessage =
                await useDashboardService().fetchDashboardMessage();
              if (responseFetchDashboardMessage) {
                set((s) => {
                  s.dashboardMessage = responseFetchDashboardMessage.data;
                });
              }
            })(),
            (async () => {
              const responseFetchAssignedTask = await useDashboardService().fetchAssignedTask();
              if (responseFetchAssignedTask) {
                set((s) => {
                  s.orgTasks = responseFetchAssignedTask.data;
                });
              }
            })(),
          ]);
        },
        authGoogleCalendars: async (code: string) => {
          const responseFromGoogleAuth = await useDashboardService().authGoogleCalendars(code);
          if (responseFromGoogleAuth) {
            set((s) => {
              s.calendars = responseFromGoogleAuth.data || [];
            });
          }
        },
        fetchGoogleCalendars: async (cb?: () => void) => {
          const responseFetchGoogleCalendars = await useDashboardService().fetchGoogleCalendars();
          if (responseFetchGoogleCalendars) {
            set((s) => {
              s.calendars = responseFetchGoogleCalendars.data || [];
              if (cb) cb();
            });
          }
        },
        removeGoogleCalendars: async (id: string, cb?: () => void) => {
          const responseRemoveGoogleCalendars = await useDashboardService().removeGoogleCalendars(
            id
          );
          if (responseRemoveGoogleCalendars) {
            set((s) => {
              s.calendars = responseRemoveGoogleCalendars.data || [];
              if (cb) cb();
            });
          }
        },

        toggleShowWorkspaces: () =>
          set((state) => ({ collapseShowWorkspaces: !state.collapseShowWorkspaces })),
        toggleCollapseFiles: () => set((state) => ({ collapseFiles: !state.collapseFiles })),
        toggleCollapseTemplate: () =>
          set((state) => ({ collapseTemplate: !state.collapseTemplate })),
        toggleCollapseKeepLearning: () =>
          set((state) => ({ collapseKeepLearning: !state.collapseKeepLearning })),
        // toggleQuickNotepad: () =>
        //   set((state) => ({ collapseQuickNotepad: !state.collapseQuickNotepad })),
        toggleCollapseRecommendation: () =>
          set((state) => ({ collapseRecommendation: !state.collapseRecommendation })),
        toggleCollapseBlog: () => set((state) => ({ collapseBlog: !state.collapseBlog })),
        toggleCollapseFavorites: () =>
          set((state) => ({ collapseFavorites: !state.collapseFavorites })),
        toggleCollapseRecentTasks: () =>
          set((state) => ({ collapseRecentTasks: !state.collapseRecentTasks })),
      },
      calendarDate: [new Date()],
      ...getInitialValue(),
    }))
  )
);

const getLofiInit = () => ({
  lofiAudio: undefined,
  isPlaying: false,
  songList: [],
  songInfo: {},
  currentSong: undefined,
});
export interface LofiState {
  lofiAudio: any;
  isPlaying: boolean;
  songList: any;
  songInfo: any;
  currentSong: any;
  actions: {
    setIsPlaying: (value: boolean) => void;
    setSongsList: (value: any) => void;
    setLofiAudio: (value: any) => void;
    setSongInfo: (value: any) => void;
    setCurrentSong: (value: any) => void;
    play: () => void;
    pause: () => void;
    nextPrev: (direction: string) => void;
  };
}

export const useLofiStore = create<
  LofiState,
  [['zustand/devtools', LofiState], ['zustand/immer', LofiState]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      actions: {
        setIsPlaying: (value) => {
          set((state) => {
            state.isPlaying = value;
          });
        },
        setSongsList: (value) => {
          set((state) => {
            state.songList = value;
          });
        },
        setLofiAudio: (value) => {
          set((state) => {
            state.lofiAudio = value;
            state.lofiAudio.onended = async () => {
              if (get().isPlaying) {
                get().actions.nextPrev('next');
              }
            };
          });
        },
        setSongInfo: (value) => {
          set((state) => {
            state.songInfo = value;
          });
        },
        setCurrentSong: (value) => {
          set((state) => {
            state.currentSong = value;
            get().lofiAudio.src = value.audio;
          });
        },
        play: () => {
          const lofiPromise = get().lofiAudio.play();
          if (lofiPromise) {
            lofiPromise
              .then((_) => {
                get().isPlaying = true;
              })
              .catch((err) => {
                get().isPlaying = false;
              });
          }
        },
        pause: () => {
          get().lofiAudio.pause();
          get().isPlaying = false;
          get().lofiAudio.currentTime = 0;
        },
        nextPrev: (direction: string) => {
          get().actions.pause();
          let currentIndex = get().songList.findIndex((song) => song.id === get().currentSong.id);
          if (direction === 'next') {
            currentIndex =
              currentIndex + 1 >= get().songList.length ? (currentIndex = 0) : currentIndex + 1;
          } else {
            currentIndex =
              currentIndex - 1 < 0 ? (currentIndex = get().songList.length - 1) : currentIndex - 1;
          }
          get().currentSong = get().songList[currentIndex];
          get().lofiAudio.src = get().currentSong.audio;
          get().actions.play();
        },
      },
      ...getLofiInit(),
    }))
  )
);
